import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FaSave, FaTag, FaBox, FaGlobe, FaHandshake, FaUserTie } from 'react-icons/fa';

const PartnerPricing = ({ productId }) => {
    const [product, setProduct] = useState(null);
    const [pricings, setPricings] = useState([]);
    const [editingRows, setEditingRows] = useState({});
    const [loading, setLoading] = useState(true);
    const [savingId, setSavingId] = useState(null);

    const getAuthHeaders = () => {
        const userInfo = JSON.parse(localStorage.getItem('user-info'));
        const origin = localStorage.getItem('user-origin');
        return {
            Authorization: `Bearer ${userInfo?.token}`,
            'Tenant-Origin': origin,
        };
    };

    const fetchProductDetails = useCallback(async () => {
        if (!productId) return;
        try {
            setLoading(true);
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const response = await axios.get(`${backendUrl}/api/products/${productId}`, {
                headers: getAuthHeaders(),
                skipLoader: true,
            });
            const data = response.data?.Data || response.data?.data;
            setProduct(data);
            setPricings(data?.pricings || []);
        } catch (error) {
            console.error('Error fetching product details:', error);
        } finally {
            setLoading(false);
        }
    }, [productId]);

    useEffect(() => {
        fetchProductDetails();
    }, [fetchProductDetails]);

    const handlePriceChange = (id, field, value) => {
        setEditingRows(prev => ({
            ...prev,
            [id]: {
                ...(prev[id] || pricings.find(p => p.id === id)),
                [field]: value
            }
        }));
    };

    const handleSave = async (id) => {
        const edited = editingRows[id];
        if (!edited) return;

        setSavingId(id);
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            // Update via the main product pricing update or a specific endpoint
            // Since we updated ProductPricing model, we'll use /api/product-pricing/{id}
            await axios.put(`${backendUrl}/api/product-pricing/${id}`, {
                ...edited,
                product_id: productId
            }, { headers: getAuthHeaders() });

            Swal.fire({ icon: 'success', title: 'Saved', timer: 1000, showConfirmButton: false });

            // Update local state
            setPricings(prev => prev.map(p => p.id === id ? { ...p, ...edited } : p));
            setEditingRows(prev => {
                const next = { ...prev };
                delete next[id];
                return next;
            });
        } catch (err) {
            console.error('Save error:', err);
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to update pricing.' });
        } finally {
            setSavingId(null);
        }
    };

 const formatCurrency = (val) => {
    if (val === null || val === undefined || val === '') return '—';

    const number = Number(String(val).replace(/[^0-9.-]+/g, ''));
    
    return number.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
};
    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status"></div>
                <p className="mt-2 text-muted">Loading license pricing...</p>
            </div>
        );
    }

    return (
        <div className="license-pricing-container"><br/><br/>
           

            <div className="table-responsive bg-white rounded shadow-sm border">
                <table className="table table-hover mb-0 align-middle" style={{ fontSize: '13px' }}>
                    <thead className="bg-light">
                        <tr>
                            <th className="px-3 py-3 text-muted fw-bold">License Model</th>
                            <th className="py-3 text-primary fw-bold text-end pe-4" style={{ width: '160px' }}>
                                <FaGlobe className="me-1" />  Price
                            </th>
                            <th className="py-3 text-info fw-bold text-end pe-4" style={{ width: '160px' }}>
                                <FaHandshake className="me-1" /> Partner Price
                            </th>
                            <th className="py-3 text-success fw-bold text-end pe-4" style={{ width: '160px' }}>
                                <FaUserTie className="me-1" /> End Customer
                            </th>
                            <th className="py-3 text-center" style={{ width: '100px' }}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pricings.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="text-center py-5 text-muted">
                                    No license models defined for this product.
                                </td>
                            </tr>
                        ) : (
                            pricings.map((pricing) => {
                                const isEditing = editingRows[pricing.id] !== undefined;
                                const row = editingRows[pricing.id] || pricing;

                                return (
                                    <tr key={pricing.id}>
                                        <td className="px-3 py-3">
                                            <span className="badge bg-soft-primary text-primary text-capitalize px-2 py-1" style={{ fontSize: '11px' }}>
                                                {pricing.license_model?.replace(/_/g, ' ')}
                                            </span>
                                        </td>
                                      
                                        <td className="text-end pe-4">
                                            <div className="input-group input-group-sm justify-content-end">
                                                <span className="input-group-text bg-light border-end-0" style={{ fontSize: '11px' }}></span>
                                                <input
                                                    type="number"
                                                    className="form-control form-control-sm text-end border-start-0"
                                                    style={{ maxWidth: '100px', fontWeight: '600' }}
                                                    value={row.price || ''}
                                                    onChange={(e) => handlePriceChange(pricing.id, 'price', e.target.value)}
                                                />
                                            </div>
                                        </td>
                                        <td className="text-end pe-4">
                                            <div className="input-group input-group-sm justify-content-end">
                                                <span className="input-group-text bg-light border-end-0" style={{ fontSize: '11px' }}></span>
                                                <input
                                                    type="number"
                                                    className="form-control form-control-sm text-end border-start-0"
                                                    style={{ maxWidth: '100px', background: row.partner_price ? '#f0faff' : 'white' }}
                                                    placeholder={formatCurrency(row.price)}
                                                    value={row.partner_price || ''}
                                                    onChange={(e) => handlePriceChange(pricing.id, 'partner_price', e.target.value)}
                                                />
                                            </div>
                                        </td>
                                        <td className="text-end pe-4">
                                            <div className="input-group input-group-sm justify-content-end">
                                                <span className="input-group-text bg-light border-end-0" style={{ fontSize: '11px' }}></span>
                                                <input
                                                    type="number"
                                                    className="form-control form-control-sm text-end border-start-0"
                                                    style={{ maxWidth: '100px', background: row.end_customer_price ? '#f0fff4' : 'white' }}
                                                    placeholder={formatCurrency(row.price)}
                                                    value={row.end_customer_price || ''}
                                                    onChange={(e) => handlePriceChange(pricing.id, 'end_customer_price', e.target.value)}
                                                />
                                            </div>
                                        </td>
                                        <td className="text-center">
                                            {isEditing ? (
                                                <button
                                                    className="btn btn-primary btn-sm rounded-circle p-1"
                                                    onClick={() => handleSave(pricing.id)}
                                                    disabled={savingId === pricing.id}
                                                    title="Save changes"
                                                >
                                                    {savingId === pricing.id ? (
                                                        <span className="spinner-border spinner-border-sm" role="status"></span>
                                                    ) : (
                                                        <FaSave size={14} />
                                                    )}
                                                </button>
                                            ) : (
                                                <span className="text-success small">
                                                    <i className="mdi mdi-check-circle-outline font-size-16"></i>
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

       
        </div>
    );
};

export default PartnerPricing;
